import React from 'react';
import { connect } from 'react-redux';
import InnerPrivate from './private';
import InnerAgent from './agent';
import { makeContactMapStateToPropsFactory, ContactPoiGetter, poiSelector } from 'components/contact/makeContactMapStateToPropsFactory';
import { ContactUnionProps, ContactType } from 'components/contact/types';
import { PoiId, PocType } from 'utils/entities';
import { isBulletinPoiType, Poi, isProjectPoiType } from 'utils/marketplaceRoutes';
import withEvents from 'analytics/withEvents';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import { getContactPageEventPayload, getPOCPhoneNumber, getProjectPhoneNumber } from 'helpers/contact';
import { Dispatch } from 'redux';
import { RootAction } from 'store/state';
import { ContactModalOpenMeta } from 'store/state/app';
import { setContactAgentOpen, submitContactAgent, contactAgentInputClick } from 'store/state/app/actions';
import { SubmitContactAgentPayload } from 'store/state/app/payloads';
import { RequestType } from 'components/contact-modal/HebrewContactModal';

const poiByIdsSelector: ContactPoiGetter = (state) => poiSelector(state);

type POIInnerContactEntryProps = ContactUnionProps & {
  id: PoiId;
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
  handleSubmitEvent?: (hasErrors: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
  onContactAgentInputClick: (eventName: string, poi: Poi) => void;
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => void;
};

export const POIInnerContactEntry: React.FC<POIInnerContactEntryProps> = (props) => {

  if (!props) return null;

  let content = null;

  switch (props.cardType) {
    case ContactType.Private:
      content = <InnerPrivate {...props} />;
      break;
    case ContactType.Agent:
      content = <InnerAgent {...props} />;
      break;
    default:
      content = null;
      break;
  }

  return (
    <div data-contact-form-type="inner">
      {content}
    </div>
  );
};

const POIInnerContactEntryWithEvents = withEvents<POIInnerContactEntryProps>((sendEvent, props) => ({
  onCallClickEvent: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => {
    const contactDetails = getContactPageEventPayload(isBulletinPoiType(props.poi) && props.poi.poc, formValues, isVisible);
    const event = {
      ...contactDetails.event,
      ...(source ? { source } : null),
      ...('poc' in props.poi && props.poi.poc && props.poi.poc.type === PocType.Agent && !isVisible ? {
        agent_details: {
          id: props.poi.poc.agentId,
          office_id: props.poi.poc.officeId,
        },
      } : null),
    };

    sendEvent(isVisible ? 'property_lead_call_submit' : 'property_lead_phone_expose', 'property', {
      ...contactDetails,
      event,
      bulletinId: props.id,
      ...(isVisible ? {
        rtbData: {
          phone: isBulletinPoiType(props.poi) && getPOCPhoneNumber(props.poi.poc) || isProjectPoiType(props.poi) && getProjectPhoneNumber(props.poi),
        },
      } : null),
    });
  },
  handleSubmitEvent: (hasErrors: boolean, formValues: IContactPageSubmitVariables, source?: string) => {
    const contactDetails = getContactPageEventPayload(isBulletinPoiType(props.poi) && props.poi.poc, formValues, null, RequestType.RequestInfo);

    const event = {
      ...contactDetails.event,
      ...(source ? { source } : null),
    };

    sendEvent(hasErrors ? 'property_lead_form_submit_attempt' : 'property_lead_form_submit', 'property', {
      ...contactDetails,
      event,
      bulletinId: props.id,
    });
  },
}))(POIInnerContactEntry);


const makeMapStateToProps = makeContactMapStateToPropsFactory(poiByIdsSelector);

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onContactPageSubmit: (payload: SubmitContactAgentPayload) => dispatch(submitContactAgent(payload)),
  onContactAgentInputClick: (eventName: string, poi: Poi) => dispatch(contactAgentInputClick({ eventName, poi })),
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => dispatch(setContactAgentOpen(id, meta)),
});

const ConnectedPOIInnerContactEntry = connect(makeMapStateToProps, mapDispatchToProps)(POIInnerContactEntryWithEvents);

export default ConnectedPOIInnerContactEntry;
